/**
 * Audit Service
 *
 * Logs significant lifecycle events to Supabase audit_log table.
 * Scope: entity creation, report finalization, entity deletion.
 * Skip: routine updates (per user preference for minimal logging).
 *
 * Audit logs are stored in Supabase only (not local IndexedDB) since
 * audit is a server-side compliance concern and keeps local DB lean.
 */

import { supabase } from '../supabase/browserClient';
import { isSupabaseSyncEnabled } from '../supabase/config';

type AuditAction = 'create' | 'finalize' | 'delete';
type AuditEntityType = 'company' | 'site' | 'unit' | 'report' | 'contact' | 'alert';

interface AuditLogEntry {
  action: AuditAction;
  entityType: AuditEntityType;
  entityId: string;
  metadata?: Record<string, unknown>;
}

/**
 * Log an audit event to Supabase.
 * Returns silently if Supabase is not configured or sync is disabled.
 *
 * @param entry - The audit log entry to record
 */
export async function logAuditEvent(entry: AuditLogEntry): Promise<void> {
  // Skip if Supabase is not available or sync is disabled
  if (!supabase || !isSupabaseSyncEnabled()) {
    return;
  }

  try {
    // Get current user
    const { data: sessionData } = await supabase.auth.getSession();
    const user = sessionData?.session?.user;

    if (!user) {
      console.warn('[Audit] No authenticated user, skipping audit log');
      return;
    }

    // Extract role from user metadata
    const userRole =
      (user.app_metadata?.role as string | undefined) ??
      (user.user_metadata?.role as string | undefined) ??
      'admin';

    // Insert audit log entry
    const { error } = await supabase.from('audit_log').insert({
      action: entry.action,
      entity_type: entry.entityType,
      entity_id: entry.entityId,
      user_id: user.id,
      user_email: user.email,
      user_role: userRole,
      metadata: entry.metadata ?? {},
    });

    if (error) {
      // Log error but don't throw - audit should not block operations
      console.error('[Audit] Failed to write audit log:', error.message);
    }
  } catch (error) {
    // Catch-all to ensure audit failures never block operations
    console.error('[Audit] Unexpected error:', error);
  }
}

/**
 * Log entity creation
 */
export async function logEntityCreated(
  entityType: AuditEntityType,
  entityId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  return logAuditEvent({
    action: 'create',
    entityType,
    entityId,
    metadata,
  });
}

/**
 * Log report finalization
 */
export async function logReportFinalized(
  reportId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  return logAuditEvent({
    action: 'finalize',
    entityType: 'report',
    entityId: reportId,
    metadata,
  });
}

/**
 * Log entity deletion
 */
export async function logEntityDeleted(
  entityType: AuditEntityType,
  entityId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  return logAuditEvent({
    action: 'delete',
    entityType,
    entityId,
    metadata,
  });
}
